import {
  Box,
  Button,
  Fab,
  Grid,
  Stack,
  Typography,
  capitalize
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useTheme } from '@mui/material/styles';
import { FC, useEffect } from 'react';
import TooltipCustomize from 'src/components/TooltipCustomize';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SearchBoxWithChip from 'src/components/SearchBoxWithChip';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import {
  reducerBumpRefreshSearchToken,
  reducerUpdatePendingKeywords,
  reducerUpdatePendingIncludeKeywords,
  reducerUpdatePendingExcludeKeywords,
  reducerApplyPendingFilters,
  reducerInitializePendingFilters
} from 'src/features/advanceSearchLandingOverview';
import ProductTourTooltip from 'src/components/ProductTourTooltip';

// INTERFACES
interface Props {
  onClick: () => void;
  title?: string;
  showBackButton?: boolean;
}

interface HeaderWrapperProps {
  isOnTour: boolean;
}

// UTILITY FUNCTIONS
const textTooltip = (title) => {
  switch (title.toLowerCase()) {
    case 'overview':
      return 'High level snapshot of talk, sentiment, and trends from multiple source types, including social media, online news, print, and broadcast.';
    case 'content':
      return 'Discover engagement performance of social profiles';
    case 'community':
      return 'Discover followers analysis and its growth of social profiles';
    case 'campaign':
      return 'Discover Hashtags performance';
    case 'conversation':
      return 'Discover all mentions and sentiment analysis of social profiles, keywords and hashtags';
    case 'comparison':
      return 'Comparison analysis between each object, group of object or label';
    default:
      return title;
  }
};

const HeaderWrapper = styled(Box)<HeaderWrapperProps>(
  ({ theme, isOnTour }) => `
    color: ${theme.header.textColor};
    padding: ${theme.spacing(1, 4, 0, 4)};
    right: 0;
    ${!isOnTour && 'z-index: 5;'}
    box-shadow: ${theme.header.boxShadow};
    justify-content: space-between;
    width: 100%;
    background: ${theme.header.background};
    @media (min-width: ${theme.breakpoints.values.md}px) {
        left: ${theme.sidebar.width};
        width: auto;
    }
`
);

const HeaderOverview: FC<Props> = ({
  title,
  onClick,
  showBackButton = true
}) => {
  // HOOKS
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const { pendingKeywords, pendingIncludeKeywords, pendingExcludeKeywords } =
    useAppSelector((state) => state.storeAdvanceSearchLandingOverview);

  const {
    isOnTour,
    currentTour: { stepNumber, menu }
  } = useAppSelector((state) => state.storeProductTour);

  // EFFECTS
  useEffect(() => {
    dispatch(reducerInitializePendingFilters());
  }, [dispatch]);

  // HELPERS
  // --

  // HANDLERS
  // -- KEYWORDS
  const handleUpdateKeywords = (value: string[]) => {
    dispatch(reducerUpdatePendingKeywords(value));
  };

  const handleDeleteKeywords = (value: string[]) => {
    dispatch(reducerUpdatePendingKeywords(value));
    // dispatch(reducerUpdateKeywords(value));
    // dispatch(reducerBumpRefreshSearchToken());
  };

  const handleClearKeywords = () => {
    dispatch(reducerUpdatePendingKeywords([]));
    // dispatch(reducerUpdateKeywords([]));
    // dispatch(reducerBumpRefreshSearchToken());
  };

  // -- INCLUDE KEYWORDS
  const handleUpdateIncludeKeywords = (value: string[]) => {
    dispatch(reducerUpdatePendingIncludeKeywords(value));
  };

  const handleDeleteIncludeKeywords = (value: string[]) => {
    dispatch(reducerUpdatePendingIncludeKeywords(value));
    // dispatch(reducerUpdateIncludeKeywords(value));
    // dispatch(reducerBumpRefreshSearchToken());
  };

  const handleClearIncludeKeywords = () => {
    dispatch(reducerUpdatePendingIncludeKeywords([]));
    // dispatch(reducerUpdateIncludeKeywords([]));
    // dispatch(reducerBumpRefreshSearchToken());
  };

  // -- EXCLUDE KEYWORDS
  const handleUpdateExcludeKeywords = (value: string[]) => {
    dispatch(reducerUpdatePendingExcludeKeywords(value));
  };

  const handleDeleteExcludeKeywords = (value: string[]) => {
    dispatch(reducerUpdatePendingExcludeKeywords(value));
    // dispatch(reducerUpdateExcludeKeywords(value));
    // dispatch(reducerBumpRefreshSearchToken());
  };

  const handleClearExcludeKeywords = () => {
    dispatch(reducerUpdatePendingExcludeKeywords([]));
    // dispatch(reducerUpdateExcludeKeywords([]));
    // dispatch(reducerBumpRefreshSearchToken());
  };

  // -- APPLY PENDING FILTERS
  const handleApplyPendingFilters = () => {
    dispatch(reducerApplyPendingFilters());
    dispatch(reducerBumpRefreshSearchToken());
  };

  const isCurrentlyOnTourStep3 =
    stepNumber === 3 && isOnTour && menu === 'overview';

  return (
    <HeaderWrapper display="flex" alignItems="center" isOnTour={isOnTour}>
      <Grid container spacing={0}>
        <Grid item md={12}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            {showBackButton && (
              <Fab
                sx={{
                  background: theme.palette.common.white,
                  border: `1px solid ${theme.palette.grey[200]}`
                }}
                size="small"
                onClick={onClick}
              >
                <ArrowBackIcon />
              </Fab>
            )}
            <Stack direction="row" alignItems="center">
              <Typography
                variant="h1"
                sx={{
                  mr: 1
                }}
              >
                {capitalize(title)}
              </Typography>
              <TooltipCustomize
                title={textTooltip(title)}
                placement="top-start"
              >
                <InfoOutlinedIcon
                  fontSize="small"
                  sx={{ transform: 'translateY(-4px)' }}
                />
              </TooltipCustomize>
            </Stack>
          </Stack>
        </Grid>

        <ProductTourTooltip placement="bottom" open={isCurrentlyOnTourStep3}>
          <Grid
            container
            spacing={1}
            mb={0}
            sx={
              isCurrentlyOnTourStep3
                ? {
                    position: 'relative',
                    zIndex: 1000
                  }
                : {}
            }
          >
            <Grid item xs={12} sm={4}>
              <SearchBoxWithChip
                title={'Search keyword or Brand'}
                placeholder={'Separated by enter'}
                onKeypressSearch={handleUpdateKeywords}
                onDeleteSearch={handleDeleteKeywords}
                onClearSearch={handleClearKeywords}
                initialValue={pendingKeywords}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <SearchBoxWithChip
                title={'Include'}
                placeholder={'Separated by enter'}
                onKeypressSearch={handleUpdateIncludeKeywords}
                onDeleteSearch={handleDeleteIncludeKeywords}
                onClearSearch={handleClearIncludeKeywords}
                initialValue={pendingIncludeKeywords}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <SearchBoxWithChip
                title={'Exclude'}
                placeholder={'Separated by enter'}
                onKeypressSearch={handleUpdateExcludeKeywords}
                onDeleteSearch={handleDeleteExcludeKeywords}
                onClearSearch={handleClearExcludeKeywords}
                initialValue={pendingExcludeKeywords}
              />
            </Grid>
          </Grid>
        </ProductTourTooltip>
        <Grid
          item
          xs={12}
          sx={{
            justifyContent: 'end',
            display: 'flex',
            marginTop: '4px',
            marginBottom: '4px',
            gap: '8px'
          }}
        >
          {/* <Button
            onClick={() => {
              dispatch(reducerClearAdvanceSearchLandingOverview());
            }}
            variant="outlined"
            sx={{
              borderRadius: '6px',
              fontFamily: 'Red Hat Display',
              fontSize: '12px',
              fontWeight: '700',
              borderColor: theme.palette.grey[400],
              color: theme.palette.grey[600],
              ':hover': {
                borderColor: theme.palette.grey[600],
                backgroundColor: theme.palette.grey[50]
              }
            }}
          >
            Clear
          </Button> */}
          <Button
            onClick={handleApplyPendingFilters}
            sx={{
              backgroundColor: theme.palette.primary.dark,
              borderRadius: '6px',
              fontFamily: 'Red Hat Display',
              fontSize: '12px',
              fontWeight: '700',
              color: theme.palette.common.white,
              ':hover': {
                backgroundColor: theme.palette.primary.dark,
                opacity: 0.8,
                color: theme.palette.common.white
              }
            }}
          >
            Submit
          </Button>
        </Grid>
      </Grid>
    </HeaderWrapper>
  );
};

export default HeaderOverview;
